import { TextField } from '@tunasong/ui-lib'
import { AutoForm } from '@tunasong/ui-lib'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { SearchSummary } from '@tunasong/models'
import type { Command } from '@tunasong/plugin-lib'
import type { Entity, EntityCommandSpec, Persisted } from '@tunasong/schemas'
import { SearchList, allQueries } from '@tunasong/search'
import {
  EventCommandSelect,
  PulseProgress,
  useDelayedValue,
  useEventCommandEntityTypes,
  Alert,
  Grid,
} from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useCallback, useState } from 'react'
import invariant from 'tiny-invariant'
import styles from './entity-event-select.module.css'

export interface EventSelectProps {
  targetEntity?: Persisted<Entity>
  onSelect: (command: EntityCommandSpec | null) => void
}

/** Select an event command for an entity */
export const EntityEventSelect: FC<EventSelectProps> = props => {
  const { targetEntity, onSelect } = props
  const [summary, setSummary] = useState<SearchSummary>()

  const { entity: commandEntity, isLoading } = graphHooks.useEntity(summary?.id)

  const [selected, setSelected] = useState<Command | null>(null)

  const handleSelect = useCallback(
    (command: Command | null) => {
      if (!commandEntity) {
        return
      }

      setSelected(command)

      const isValid = command && !command.cmdParamsSchema

      if (isValid) {
        onSelect({
          entityId: commandEntity.id,
          commandId: command.id,
          label: command.name,
        })
      } else {
        onSelect(null)
      }
    },
    [commandEntity, onSelect]
  )

  const handleCommandParams = useCallback(
    (data: Record<string, unknown>) => {
      invariant(selected)
      invariant(commandEntity?.id)

      onSelect({
        entityId: commandEntity.id,
        commandId: selected.id,
        label: selected.name,
        commandParams: data,
      })
    },
    [commandEntity?.id, onSelect, selected]
  )
  const entityTypes = useEventCommandEntityTypes()
  const [queryString, setQueryString] = useState<string>()
  const queryValue = useDelayedValue(queryString, 500)
  const query = queryValue ? allQueries.type({ types: entityTypes, query: queryValue }) : undefined
  const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setQueryString(value)
  }

  return (
    <Grid container className={styles.grid} spacing={2}>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          size="small"
          className={styles.search}
          value={queryString}
          onChange={handleQueryChange}
          label="Search"
          fullWidth
        />
        <Alert severity="info">Entity types: {entityTypes.join(', ')}</Alert>

        <SearchList
          query={query}
          intent={{ query: queryValue, types: entityTypes }}
          onChange={setSummary}
          selected={summary}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        {isLoading ? <PulseProgress /> : null}
        <EventCommandSelect
          className={styles.commandSelect}
          entity={commandEntity}
          targetEntity={targetEntity}
          onClick={handleSelect}
          selected={selected}
          showOnlySelected={true}
        />
        {selected?.cmdParamsSchema ? (
          <AutoForm schema={selected.cmdParamsSchema} onSubmit={handleCommandParams} />
        ) : null}
      </Grid>
    </Grid>
  )
}

export default EntityEventSelect
